import { get, post } from "@/common/services/httpClient";
import { useAppQuery } from "@/common/hooks/useAppQuery";

// ========== TYPES ==========

export interface CustomerException {
    id: number;
    code: string;
    name: string;
    customerId?: number;
    customerName?: string;
}

export interface DropdownOption {
    cardNumber?: string;
    label: string;
    policyHolderName?: string;
    value: string | number;
    planId?: number | undefined;
    customerId?: number | undefined;
    customerExceptions?: any[];
    optionColor?: string;
    expiryDate?: string;
    [key: string]: unknown;
}

type DropdownDataItem = Record<string, unknown>;

interface PostDropdownResponse {
    data?: {
        [key: string]: DropdownDataItem[];
    };
    [key: string]: DropdownDataItem[] | unknown;
}

// ========== GENERIC DROPDOWN QUERY HOOKS ==========

function usePostDropdownQuery(params: {
    queryKey: string;
    apiUrl: string;
    body: object;
    dataPath: string;
    labelKey: string;
    valueKey: string;
    enabled?: boolean;
}) {
    const { queryKey, apiUrl, body, dataPath, labelKey, valueKey, enabled = true } = params;

    return useAppQuery<DropdownOption[]>(
        [queryKey, apiUrl, body],
        async () => {
            const res = await post<PostDropdownResponse>(apiUrl, body);
            const data = (res.data?.[dataPath] ?? res[dataPath]) as DropdownDataItem[];
            console.log('data', data);

            if (data && Array.isArray(data)) {
                return data.map((item) => {
                    let value = item[valueKey];
                    // Ensure value is string or number, fallback to empty string
                    if (typeof value !== 'string' && typeof value !== 'number') {
                        value = value && value.toString ? value.toString() : '';
                    }
                    const mapped: DropdownOption = {
                        ...item,
                        label: String(item[labelKey] ?? ''),
                        value: value as string | number,
                        customerExceptions: Array.isArray(item.customerExceptions) ? item.customerExceptions : [], // preserve exceptions
                    };
                    console.log('Mapped dropdown item:', mapped); // ✅ Debug each item
                    return mapped;
                });
            }
            return [];
        },
        {
            enabled,
        }
    );
}

// ========== API ENDPOINTS ==========
const GET_POLICYHOLDERS_DROPDOWN_API = "Claim/GetPolicyHoldersDropDown";
const GET_PATIENTS_DROPDOWN_API = "Claim/GetPatientsDropDown";
const GET_DROPDOWN_DATA_API = "Lookup/GetDropdownData";
const GET_MEDICAL_PROVIDERS_API = "Claim/GetMedicalProviders";
const GET_SERVICEITEMS_DROPDOWN_API = "ServiceCategoryItem/Search";
const GET_SERVICEITEMS_DROPDOWN_DEP_API = "Claim/GetMedicalProviderServiceItem";
const GET_BANKDETAILS_DROPDOWN_API = "BankDetail/GetByPolicyHolderId";
const GET_PLAN_SERVICE_DROPDOWN_API = "Plan/GetServices";

// ========== SPECIFIC HOOKS for ClaimRequestsForm ==========

export const usePolicyHolderDropdown = () =>
    useAppQuery<DropdownOption[]>(
        ["policyHolderDropdown"],
        async () => {
            const res = await get<{ data: { id: number, name: string, cardNumber?: string, policyHolderName?: string }[] }>(
                GET_POLICYHOLDERS_DROPDOWN_API
            );
            if (res.data && Array.isArray(res.data)) {
                return res.data.map((item) => ({
                    label: item.cardNumber ? `${item.name} - ${item.cardNumber}` : item.name,
                    value: item.id,
                    cardNumber: item.cardNumber,
                    policyHolderName: item.name,
                    ...item
                }));
            }
            return [];
        }
    );

export const usePatientDropdown = (policyHolderId: number | undefined) =>
    useAppQuery<DropdownOption[]>(
        ["patientDropdown", policyHolderId],
        async () => {
            if (!policyHolderId) return [];
            const res = await get<{ data: { id: number, name: string, cardNumber: string, patientName?: string }[] }>(
                `${GET_PATIENTS_DROPDOWN_API}/${policyHolderId}`
            );
            if (res.data) {
                return res.data.map((item) => ({
                    label: `${item.name}`,
                    value: item.id,
                    patientName: item.name,
                    ...item
                }));
            }
            return [];
        },
        {
            enabled: !!policyHolderId,
        }
    );

export const useMedicalProviderDropdown = (isInNetwork: boolean) =>
    usePostDropdownQuery({
        queryKey: "medicalProviderDropdown",
        apiUrl: GET_MEDICAL_PROVIDERS_API,
        body: { isInNetwork: isInNetwork },
        dataPath: "data",
        labelKey: "name",
        valueKey: "id",
    });

export const useServiceItemDropdown = () =>
    usePostDropdownQuery({
        queryKey: "serviceItemDropdown",
        apiUrl: GET_SERVICEITEMS_DROPDOWN_API,
        body: { pageNumber: 0, pageSize: 0 },
        dataPath: "data",
        labelKey: "name",
        valueKey: "id",
    });

export const useServiceItemDropdownDependency = (medicalProviderId: number | undefined, planId: number | undefined, isInNetwork: boolean | undefined, policyHolderId: number | undefined, customerId: number | undefined) =>
    usePostDropdownQuery({
        queryKey: "ServiceItemDropdownList",
        apiUrl: GET_SERVICEITEMS_DROPDOWN_DEP_API,
        body: { medicalProviderId: medicalProviderId, planId: planId, isInNetwork: isInNetwork, policyHolderId: policyHolderId, customerId: customerId },
        dataPath: "data",
        labelKey: "label",
        valueKey: "serviceCategoryItemId",
        enabled: medicalProviderId !== undefined && policyHolderId !== undefined && planId !== undefined && isInNetwork !== undefined && customerId !== undefined 
    });

export const usePlanServiceDropdown = (planId: number | undefined, policyHolderId: number | undefined) =>
    useAppQuery<DropdownOption[]>(
        ["planServiceDropdown", policyHolderId],
        async () => {
            if (!policyHolderId) return [];
            const res = await get<{ data: { serviceCategoryItemId: number, serviceCategoryItemName: string }[] }>(
                `${GET_PLAN_SERVICE_DROPDOWN_API}?planId=${planId}&policyHolderId=${policyHolderId}`
            );
            if (res.data) {
                console.log('response exists....');
                return res.data.map((item) => ({
                    label: `${item.serviceCategoryItemName}`,
                    value: item.serviceCategoryItemId,
                    ...item
                }));
            }
            return [];
        },
        {
            enabled: !!policyHolderId,
        }
    );

export const useBankDetailDropdown = (policyHolderId: number | undefined) =>
    useAppQuery<DropdownOption[]>(
        ["bankDetailDropdown", policyHolderId],
        async () => {
            if (!policyHolderId) return [];
            const res = await get<{ data: { id: number, bankName: string, accountNo: string }[] }>(
                `${GET_BANKDETAILS_DROPDOWN_API}/${policyHolderId}`
            );
            if (res.data) {
                return res.data.map((item) => ({
                    label: `${item.bankName} - ${item.accountNo}`,
                    value: item.id,
                }));
            }
            return [];
        },
        {
            enabled: !!policyHolderId,
        }
    );



export const useCurrencyDropdown = () =>
    usePostDropdownQuery({
        queryKey: "currencyDropdown",
        apiUrl: GET_DROPDOWN_DATA_API,
        body: { dropdowns: ["currency"] },
        dataPath: "currency",
        labelKey: "Name",
        valueKey: "Id",
    });

export const useCountryDropdown = () =>
    usePostDropdownQuery({
        queryKey: "countryDropdown",
        apiUrl: GET_DROPDOWN_DATA_API,
        body: { dropdowns: ["country"] },
        dataPath: "country",
        labelKey: "Name",
        valueKey: "Id",
    });